/* ============================================================
   Corporate Structure Designer

   Five questions, one recommended shape, and a list of questions to put
   to counsel.

   The thing this tool refuses to do is the thing that makes it worth
   using. It quotes no tax rates and no treaty positions — see the note
   at the top of data/structure.js for why. What it does instead is
   settle the structural question, which is stable and knowable, and
   then generate the specific questions that the answers raise, so the
   hour with a professional starts from something better than "what
   structure should we use?".

   The most common real answer is also the least commercial one: for a
   single-market, self-funded operator with nothing in view, one company
   is correct. The tool says so plainly rather than manufacturing
   complexity it can then charge to explain.
   ============================================================ */

import { QUESTIONS, selectArchetype, counselAgenda } from '../../data/structure.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { PrintButton } from '../../components/PrintButton.js';

export function StructurePage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Corporate Structure Designer</h1>
      <p>
        How many entities, in what relationship, and what each arrangement costs to carry —
        for a logistics business trading across African markets.
      </p>
    </header>

    <div class="notice notice-info" style="margin-bottom: var(--ca-space-5);">
      <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
      <div style="font-size: var(--ca-text-xs);">
        <strong style="font-weight: var(--ca-weight-medium);">No rates here, deliberately.</strong>
        Withholding rates and treaty positions change with each finance act and turn on facts
        about a specific taxpayer. Everything else in this product carries a source and a date;
        there is no honest way to do that for a treaty position, so this tool settles the
        structure and hands you the questions to price with counsel.
      </div>
    </div>

    <div id="str-form">
      ${QUESTIONS.map(
        (q) => html`
          <section class="section">
            <h2 class="section-title">${q.label}</h2>
            <p class="section-caption">${q.hint}</p>
            <div class="card">
              <div class="stack" style="gap: var(--ca-space-3);">
                ${q.options.map(
                  (o) => html`
                    <label class="row" style="gap: var(--ca-space-3); cursor: pointer; align-items: flex-start;">
                      <input type="radio" name="str-${q.id}" value="${o.value}"
                             style="margin-top: 3px; width: 17px; height: 17px; accent-color: var(--brand-accent); flex-shrink:0;" />
                      <span style="flex:1;">
                        <span style="display:block; font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
                          ${o.label}
                        </span>
                        <span style="display:block; font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary);">
                          ${o.note}
                        </span>
                      </span>
                    </label>
                  `
                )}
              </div>
            </div>
          </section>
        `
      )}

      <div id="str-error"></div>

      <button id="str-design" class="btn btn-primary btn-block">
        Recommend a structure
      </button>
    </div>

    <div id="structure-results" hidden></div>
  `;

  page.querySelector('#str-design').addEventListener('click', () => design(page));
  return page;
}

function design(page) {
  const answers = {};
  const unanswered = [];
  for (const q of QUESTIONS) {
    const picked = page.querySelector(`input[name="str-${q.id}"]:checked`);
    if (picked) answers[q.id] = picked.value;
    else unanswered.push(q.label);
  }

  const errorSlot = page.querySelector('#str-error');
  if (unanswered.length > 0) {
    errorSlot.innerHTML = html`
      <div class="notice notice-danger" style="margin-bottom: var(--ca-space-4);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div>
          <strong style="font-weight: var(--ca-weight-medium);">Still to answer</strong>
          <ul style="margin: var(--ca-space-1) 0 0 var(--ca-space-4); font-size: var(--ca-text-xs);">
            ${unanswered.map((l) => html`<li>${l}</li>`)}
          </ul>
        </div>
      </div>
    `;
    errorSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }
  errorSlot.innerHTML = '';

  const selection = selectArchetype(answers);
  const agenda = counselAgenda(answers, selection);
  renderResults(page, answers, selection, agenda);
}

function renderResults(page, answers, selection, agenda) {
  page.querySelector('#str-form').hidden = true;

  const results = page.querySelector('#structure-results');
  results.hidden = false;
  results.className = 'animate-fade-in';
  results.replaceChildren();

  const a = selection.archetype;

  /* ---- The recommendation ---- */
  const head = document.createElement('div');
  head.style.cssText = 'text-align:center; margin-bottom: var(--ca-space-6);';
  head.innerHTML = html`
    <div class="badge">RECOMMENDED SHAPE</div>
    <h2 style="margin-top: var(--ca-space-3); font-size: var(--ca-text-xl);">${a.label}</h2>
    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-top: var(--ca-space-3); max-width: 50ch; margin-inline: auto;">
      ${a.summary}
    </p>
  `;
  results.appendChild(head);

  /* ---- The shape, drawn as a stack ---- */
  const shape = document.createElement('div');
  shape.className = 'card';
  shape.style.marginBottom = 'var(--ca-space-4)';
  shape.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-4);">The entities</h3>
    <div class="stack" style="gap: 0;">
      ${a.shape.map(
        (entity, i) => html`
          ${i > 0
            ? html`<div style="width:2px; height:14px; background: var(--ca-border); margin-left: calc(var(--ca-space-4) + 1px);"></div>`
            : ''}
          <div class="row" style="gap: var(--ca-space-3); align-items:center; padding: var(--ca-space-3); border:1px solid var(--ca-border); border-radius: var(--ca-radius-sm); background: var(--ca-surface-sunk);">
            <span style="flex-shrink:0; color: var(--ca-text-tertiary);">${raw(icon(i === 0 ? 'group' : 'setting', 15))}</span>
            <span style="font-size: var(--ca-text-sm);">${entity}</span>
          </div>
        `
      )}
    </div>
  `;
  results.appendChild(shape);

  /* ---- Why this one ---- */
  const why = document.createElement('div');
  why.className = 'card';
  why.style.marginBottom = 'var(--ca-space-4)';
  why.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">Why this one, on your answers</h3>
    <ul style="margin:0; padding-left: var(--ca-space-4); font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
      ${selection.reasons.map((r) => html`<li style="margin-bottom: var(--ca-space-2);">${r}</li>`)}
    </ul>
    <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin: var(--ca-space-3) 0 0;">
      <strong style="font-weight: var(--ca-weight-medium);">What it solves.</strong> ${a.solves}
    </p>
  `;
  results.appendChild(why);

  /* ---- What it costs to carry ---- */
  const carries = document.createElement('div');
  carries.className = 'card';
  carries.style.marginBottom = 'var(--ca-space-4)';
  carries.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">What it costs to carry, every year</h3>
    <ul style="margin:0 0 var(--ca-space-4); padding-left: var(--ca-space-4); font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
      ${a.carries.map((c) => html`<li style="margin-bottom: var(--ca-space-2);">${c}</li>`)}
    </ul>
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">Where this shape goes wrong</h3>
    <ul style="margin:0; padding-left: var(--ca-space-4); font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
      ${a.breaks.map((b) => html`<li style="margin-bottom: var(--ca-space-2);">${b}</li>`)}
    </ul>
  `;
  results.appendChild(carries);

  /* ---- Also worth considering ---- */
  if (selection.alsoConsider) {
    const also = document.createElement('div');
    also.className = 'card';
    also.style.marginBottom = 'var(--ca-space-4)';
    also.innerHTML = html`
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">Also worth considering</h3>
      <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin:0 0 var(--ca-space-2);">
        <strong style="font-weight: var(--ca-weight-medium); color: var(--ca-text-primary);">${selection.alsoConsider.label}.</strong>
        ${selection.alsoConsider.summary}
      </p>
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin:0;">
        This sits underneath the recommended shape rather than replacing it.
      </p>
    `;
    results.appendChild(also);
  }

  /* ---- The agenda for counsel ---- */
  const ag = document.createElement('div');
  ag.className = 'card';
  ag.style.marginBottom = 'var(--ca-space-4)';
  if (agenda.length > 0) {
    ag.innerHTML = html`
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">
        Take these to counsel
      </h3>
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin: 0 0 var(--ca-space-4);">
        ${agenda.length} question${agenda.length === 1 ? '' : 's'}, each raised by something you
        answered. This is where the rates absent from this tool get priced by someone who can be
        responsible for the answer.
      </p>
      <div class="stack" style="gap: var(--ca-space-4);">
        ${agenda.map(
          (item, i) => html`
            <div>
              <div class="row" style="gap: var(--ca-space-2); align-items: baseline;">
                <span class="num" style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); flex-shrink:0;">${i + 1}</span>
                <span class="badge">${item.trigger}</span>
              </div>
              <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin: var(--ca-space-2) 0 0 var(--ca-space-4);">
                ${item.ask}
              </p>
            </div>
          `
        )}
      </div>
    `;
  } else {
    /* Genuinely reachable: one market, founder-funded, asset-light,
       nothing in view. An empty section would read as a broken render,
       so it says what it means. */
    ag.innerHTML = html`
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">Nothing to take to counsel yet</h3>
      <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin:0;">
        On these answers there is no cross-border charge, no outside investor and no transaction
        in view — so there is no structuring question outstanding. Come back when one of those
        changes; that is the point at which the answer starts to matter.
      </p>
    `;
  }
  results.appendChild(ag);

  /* ---- Limits ---- */
  const caveat = document.createElement('div');
  caveat.className = 'notice notice-warning';
  caveat.style.marginBottom = 'var(--ca-space-4)';
  caveat.innerHTML = html`
    <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
    <div style="font-size: var(--ca-text-xs);">
      <strong style="font-weight: var(--ca-weight-medium);">This is not legal or tax advice.</strong>
      It is a structural starting point built from five answers, and it cannot see your
      contracts, your licences, your shareholders or your history. Company law and revenue
      practice differ in every market named here and change without notice. Nothing above
      should be implemented without advice from someone admitted in the relevant jurisdiction.
    </div>
  `;
  results.appendChild(caveat);

  const actions = document.createElement('div');
  actions.className = 'row';
  actions.style.cssText = 'gap: var(--ca-space-3); flex-wrap: wrap;';
  const again = document.createElement('button');
  again.className = 'btn btn-secondary';
  again.textContent = 'Change the answers';
  again.addEventListener('click', () => {
    page.querySelector('#structure-results').hidden = true;
    page.querySelector('#str-form').hidden = false;
    page.querySelector('#str-form').scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  actions.appendChild(again);
  actions.appendChild(
    PrintButton({
      targetId: 'structure-results',
      title: 'Corporate Structure',
      subtitle: a.label,
    })
  );
  results.appendChild(actions);

  results.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
